// frontend/src/hooks/useAssetOwners.ts — the asset-owners read surface: the
// attached list (member-read — the row carries every field, so no detail hook)
// and the cross-project pool search behind the Add flow. Keys share the
// `['asset-owners', projectId]` prefix so a mutation's invalidate refreshes list +
// search together.

import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  AssetOwner,
  AssetOwnerListResponse,
  AssetOwnerSearchResponse,
} from '@ems-app/schemas/asset-owners/responses';
import { apiClient } from '../lib/apiClient.js';
import { useDebouncedValue } from './useDebouncedValue.js';
import type { SmallSource } from './useOptions.js';

export const assetOwnersKeys = {
  /** List key — also the invalidation PREFIX (covers search). */
  all: (projectId: string) => ['asset-owners', projectId] as const,
  search: (projectId: string, q: string) => ['asset-owners', projectId, 'search', q] as const,
};

/** The attached list read — the page's, and a form's Load the latest (an owner has no detail read: the list row is its
 *  read). */
export async function fetchAssetOwners(projectId: string): Promise<AssetOwnerListResponse> {
  const r = await apiClient.get<AssetOwnerListResponse>(`/projects/${projectId}/asset-owners`);
  return r.data;
}

// Module-level selector (referentially stable, §State select doctrine).
const selectItems = (r: AssetOwnerListResponse): AssetOwner[] => r.items;

export function useAssetOwners(projectId: string, enabled = true): UseQueryResult<AssetOwner[], Error> {
  return useQuery({
    queryKey: assetOwnersKeys.all(projectId),
    queryFn: () => fetchAssetOwners(projectId),
    enabled: enabled && projectId.length > 0,
    select: selectItems,
  });
}

/** The attached owners as a picker's source (ADR-080 §3 — a SMALL list asked whole on the press, the page's own cache
 *  entry; a disabled owner hidden at rest, shown marked when chosen or typed — an edit keeps showing what the books say). */
export function ownersSource(projectId: string): SmallSource<AssetOwnerListResponse> {
  return {
    kind: 'small',
    queryKey: assetOwnersKeys.all(projectId),
    fetch: () => fetchAssetOwners(projectId),
    rows: (r) => r.items.map((o) => ({ id: o.id, label: o.name, pickable: true, inactive: o.is_disabled, group: null })),
  };
}

async function fetchAssetOwnerSearch(projectId: string, q: string): Promise<AssetOwnerSearchResponse> {
  const r = await apiClient.get<AssetOwnerSearchResponse>(`/projects/${projectId}/asset-owners/search`, { params: { q } });
  return r.data;
}

/** The pool search (q required, capped + `has_more`) — the useUserSearch shape:
 *  debounced internally, keeps the prior results while the next query is in
 *  flight, `isSearching` while the typed query hasn't settled. */
export function useAssetOwnerSearch(
  projectId: string,
  query: string,
  debounceMs = 300,
): UseQueryResult<AssetOwnerSearchResponse, Error> & { isSearching: boolean } {
  const trimmed = query.trim();
  const q = useDebouncedValue(trimmed, debounceMs);
  const result = useQuery({
    queryKey: assetOwnersKeys.search(projectId, q),
    queryFn: () => fetchAssetOwnerSearch(projectId, q),
    enabled: projectId.length > 0 && q.length > 0,
    placeholderData: keepPreviousData,
  });
  const isSearching = trimmed.length > 0 && (q !== trimmed || result.isFetching);
  return Object.assign(result, { isSearching });
}
